/*
 * Shilajit Global — cart drawer conversion layer (v2, 16 Sep 2026).
 *
 * Injects five blocks into Dawn's stock cart drawer without forking
 * snippets/cart-drawer.liquid:
 *
 *   header side (before <cart-drawer-items>)
 *     1. Dispatch countdown — counts down to the REAL same-day dispatch
 *        cutoff (default 2pm Mon–Fri). Never a fake "reserved" timer: the
 *        DMCC Act 2024 bans false time-limits, so the only urgency here is
 *        the one that is true.
 *     2. Trust banner — rating and count computed from the Judge.me review
 *        metafields at render time (never a typed-in number), reviewer
 *        initials as avatars.
 *   footer side (top of .drawer__footer, above Dawn's totals)
 *     3. Two-tier spend bar — free delivery (per-market threshold from
 *        snippets/sg-free-threshold.liquid) then a free gift. The gift is
 *        auto-added as a hidden-property line and made free by an automatic
 *        Buy-X-Get-Y discount in admin; if that discount is ever missing the
 *        script notices the non-zero price, removes the line and stops.
 *     4. Discount code — applied inside the drawer via /cart/update.js
 *        `discount` (Cart AJAX API, May 2025). Falls back to the /discount/
 *        redirect if the API ever stops returning discount_codes.
 *     5. Delivery estimate — dispatch-day logic + working-day transit window.
 *   plus the existing cross-sell cards (sg-cart-upsell.liquid), unchanged.
 *
 * Dawn replaces the drawer's innerHTML on every cart change through the
 * Section Rendering API, so a MutationObserver re-injects whenever a fresh
 * footer (no data-sg-enhanced flag) appears. Config: window.sgCart from
 * sections/sg-cart-boot.liquid; window.sgUpsell from sg-cart-upsell.
 */
(function () {
  'use strict';

  var cfg = window.sgCart;
  if (!cfg) return;

  var upsellConfig = window.sgUpsell;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SECTIONS = 'cart-drawer,cart-icon-bubble';

  var busy = false;
  var lastCart = null;
  var discountState = { error: '', pending: '' };
  var giftState = { syncing: false, declined: false, broken: false, hadGift: false };

  try {
    giftState.declined = sessionStorage.getItem('sg-gift-declined') === '1';
    giftState.broken = sessionStorage.getItem('sg-gift-broken') === '1';
  } catch (e) { /* storage blocked — session flags simply don't persist */ }

  /* ---------------------------------------------------------------- money */

  function formatMoney(cents, noDecimals) {
    // shop.money_format can carry markup ("<span class=money>£{{amount}}</span>"),
    // so the number is rendered into the format string instead of prefixing £.
    var value = (cents / 100).toFixed(2);
    var whole = String(Math.round(cents / 100));
    var format = cfg.moneyFormat || '£{{amount}}';
    var out = format
      .replace(/{{\s*amount\s*}}/g, noDecimals ? whole : value)
      .replace(/{{\s*amount_no_decimals\s*}}/g, whole)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, noDecimals ? whole : value.replace('.', ','))
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, whole);
    return out;
  }

  function el(tag, className, html) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ------------------------------------------------------- dispatch clock */

  var d = cfg.dispatch || {};
  var TZ = d.timezone || 'Europe/London';
  var CUTOFF_H = typeof d.cutoffHour === 'number' ? d.cutoffHour : 14;
  var CUTOFF_M = typeof d.cutoffMinute === 'number' ? d.cutoffMinute : 0;
  var WORK_DAYS = (d.days || [1, 2, 3, 4, 5]).map(Number); // 0 = Sunday
  var TRANSIT_MIN = d.transitMin || 3;
  var TRANSIT_MAX = d.transitMax || 5;
  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  var partsFormatter = null;
  try {
    partsFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: TZ, hourCycle: 'h23',
      year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', second: 'numeric'
    });
  } catch (e) { partsFormatter = null; }

  // Wall-clock "now" in the shop's timezone, as plain numbers.
  function nowInZone() {
    var now = new Date();
    if (!partsFormatter) {
      return { y: now.getFullYear(), m: now.getMonth() + 1, d: now.getDate(),
        h: now.getHours(), mi: now.getMinutes(), s: now.getSeconds() };
    }
    var o = {};
    partsFormatter.formatToParts(now).forEach(function (p) { o[p.type] = p.value; });
    return { y: +o.year, m: +o.month, d: +o.day, h: +o.hour % 24, mi: +o.minute, s: +o.second };
  }

  // Calendar arithmetic on UTC-noon stamps so DST can never shift a day.
  function stamp(y, m, dd) { return Date.UTC(y, m - 1, dd, 12); }
  function addDays(t, n) { return t + n * 86400000; }
  function weekday(t) { return new Date(t).getUTCDay(); }
  function isWorkDay(t) { return WORK_DAYS.indexOf(weekday(t)) !== -1; }
  function nextWorkDay(t) { // strictly after t
    var n = addDays(t, 1), guard = 0;
    while (!isWorkDay(n) && guard++ < 14) n = addDays(n, 1);
    return n;
  }
  function addWorkDays(t, n) { var c = t; for (var i = 0; i < n; i++) c = nextWorkDay(c); return c; }
  function fmtDay(t, withMonth) {
    var x = new Date(t);
    return DAY_SHORT[x.getUTCDay()] + ' ' + x.getUTCDate() + (withMonth ? ' ' + MON_SHORT[x.getUTCMonth()] : '');
  }

  function dispatchInfo() {
    if (!WORK_DAYS.length) return null;
    var n = nowInZone();
    var today = stamp(n.y, n.m, n.d);
    var secsNow = n.h * 3600 + n.mi * 60 + n.s;
    var secsCutoff = CUTOFF_H * 3600 + CUTOFF_M * 60;
    var sameDay = isWorkDay(today) && secsNow < secsCutoff;
    var dispatch = sameDay ? today : nextWorkDay(today);
    var label;
    if (sameDay) label = 'today';
    else if (dispatch === addDays(today, 1)) label = 'tomorrow';
    else label = DAY_NAMES[weekday(dispatch)];
    var from = addWorkDays(dispatch, TRANSIT_MIN);
    var to = addWorkDays(dispatch, TRANSIT_MAX);
    var sameMonth = new Date(from).getUTCMonth() === new Date(to).getUTCMonth();
    return {
      sameDay: sameDay,
      secondsLeft: sameDay ? secsCutoff - secsNow : 0,
      dispatchLabel: label,
      window: fmtDay(from, !sameMonth) + ' – ' + fmtDay(to, true)
    };
  }

  function cutoffLabel() {
    var h12 = CUTOFF_H % 12 || 12;
    var suffix = CUTOFF_H < 12 ? 'am' : 'pm';
    return h12 + (CUTOFF_M ? ':' + (CUTOFF_M < 10 ? '0' : '') + CUTOFF_M : '') + suffix;
  }

  function hms(secs) {
    var h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60;
    return h + ':' + (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
  }

  /* ------------------------------------------------ 1. dispatch countdown */

  var countdownTimer = null;
  var countdownSameDay = null;

  function buildCountdown() {
    if (!cfg.show || !cfg.show.countdown) return null;
    var info = dispatchInfo();
    if (!info) return null;

    var wrap = el('div', 'sg-cd-countdown');
    wrap.setAttribute('data-sg-countdown', '');
    wrap.setAttribute('role', 'status');
    wrap.setAttribute('aria-live', 'off');

    var icon = '<svg class="sg-cd-countdown__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="8.5"></circle><path d="M12 7.5V12l3 2"></path></svg>';

    if (info.sameDay) {
      wrap.innerHTML = icon +
        '<span class="sg-cd-countdown__text">Order within <strong class="sg-cd-countdown__time" data-sg-cd-time>' + hms(info.secondsLeft) + '</strong> for dispatch today</span>' +
        '<span class="sg-cd-countdown__chip">' + cutoffLabel() + ' cutoff</span>';
    } else {
      wrap.innerHTML = icon +
        '<span class="sg-cd-countdown__text">Order now — dispatched <strong>' + info.dispatchLabel + '</strong></span>';
    }
    countdownSameDay = info.sameDay;
    return wrap;
  }

  function tick() {
    var nodes = document.querySelectorAll('[data-sg-countdown]');
    if (!nodes.length) return;
    var info = dispatchInfo();
    if (!info) return;
    if (info.sameDay !== countdownSameDay) {
      // Crossed the cutoff while the drawer was open: rebuild the block and
      // the delivery estimate together so they never disagree.
      Array.prototype.forEach.call(nodes, function (n) {
        var fresh = buildCountdown();
        if (fresh) n.replaceWith(fresh); else n.remove();
      });
      Array.prototype.forEach.call(document.querySelectorAll('[data-sg-delivery]'), function (n) {
        var fresh = buildDelivery();
        if (fresh) n.replaceWith(fresh); else n.remove();
      });
      return;
    }
    Array.prototype.forEach.call(nodes, function (n) {
      var t = n.querySelector('[data-sg-cd-time]');
      if (t) {
        t.textContent = hms(info.secondsLeft);
        n.classList.toggle('sg-cd-countdown--soon', info.secondsLeft <= 1800);
      }
    });
  }

  function startClock() {
    if (countdownTimer) return;
    countdownTimer = setInterval(tick, 1000);
  }

  /* --------------------------------------------------- 2. trust banner */

  function reviewStats() {
    var list = cfg.reviews || [];
    var count = 0, weighted = 0;
    list.forEach(function (x) {
      var c = parseInt(x.c, 10);
      if (!c || c < 1) return;
      var r = x.r;
      if (typeof r === 'string') {
        try { r = JSON.parse(r); } catch (e) { r = parseFloat(r); }
      }
      if (r && typeof r === 'object') r = parseFloat(r.rating != null ? r.rating : r.value);
      if (typeof r !== 'number' || isNaN(r)) return;
      count += c;
      weighted += c * r;
    });
    if (!count) return null;
    return { count: count, avg: Math.round((weighted / count) * 10) / 10 };
  }

  function starSvg(avg, uid) {
    var path = 'M8 1l2.1 4.5 4.9.6-3.6 3.4.9 4.9L8 12l-4.3 2.4.9-4.9L1 6.1l4.9-.6z';
    var out = '<svg class="sg-cd-trust__stars" width="88" height="16" viewBox="0 0 88 16" aria-hidden="true"><defs>';
    var i, fill;
    for (i = 0; i < 5; i++) {
      var f = Math.max(0, Math.min(1, avg - i));
      out += '<linearGradient id="' + uid + i + '" x1="0" x2="1"><stop offset="' + (f * 100) + '%" stop-color="currentColor"></stop><stop offset="' + (f * 100) + '%" stop-color="rgba(0,0,0,0.14)"></stop></linearGradient>';
    }
    out += '</defs>';
    for (i = 0; i < 5; i++) {
      fill = 'url(#' + uid + i + ')';
      out += '<path fill="' + fill + '" transform="translate(' + (i * 18) + ' 0)" d="' + path + '"></path>';
    }
    return out + '</svg>';
  }

  function buildTrust() {
    if (!cfg.show || !cfg.show.trust) return null;
    var stats = reviewStats();
    if (!stats || stats.count < (cfg.minReviews || 1)) return null;

    var wrap = el('div', 'sg-cd-trust');
    wrap.setAttribute('data-sg-trust', '');

    var initials = (cfg.avatarInitials || []).slice(0, 4);
    var avatars = '';
    if (initials.length) {
      avatars = '<div class="sg-cd-trust__avatars" aria-hidden="true">' + initials.map(function (s, i) {
        return '<span class="sg-cd-trust__avatar sg-cd-trust__avatar--' + (i % 3) + '">' + escapeHtml(String(s).trim().charAt(0).toUpperCase()) + '</span>';
      }).join('') + '</div>';
    }

    var uid = 'sgstar' + Math.floor(Math.random() * 1e6);
    var noun = stats.count === 1 ? 'verified buyer' : 'verified buyers';
    wrap.innerHTML = avatars +
      '<div class="sg-cd-trust__body">' +
        '<div class="sg-cd-trust__row">' + starSvg(stats.avg, uid) +
          '<span class="sg-cd-trust__score">' + stats.avg.toFixed(1) + ' / 5</span></div>' +
        '<span class="sg-cd-trust__note">From ' + stats.count + ' ' + noun + ' via Judge.me</span>' +
      '</div>';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', 'Rated ' + stats.avg.toFixed(1) + ' out of 5 from ' + stats.count + ' ' + noun + ' via Judge.me');
    return wrap;
  }

  /* ---------------------------------------------------- gift helpers */

  function giftLine(cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      if (p._sg_gift) return items[i];
    }
    return null;
  }

  function giftActive() {
    return !!(cfg.gift && cfg.gift.variantId && cfg.gift.threshold && !giftState.declined && !giftState.broken);
  }

  // Spend that counts towards the tiers: everything after discounts, minus
  // whatever the gift line itself costs (normally £0).
  function qualifyingTotal(cart) {
    var g = giftLine(cart);
    return cart.total_price - (g ? g.final_line_price : 0);
  }

  function rememberGift(key, on) {
    try { if (on) sessionStorage.setItem(key, '1'); else sessionStorage.removeItem(key); } catch (e) { /* ignore */ }
  }

  function cartRequest(path, body) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function rerender(state) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !state || !state.sections) { refresh(); return; }

    if (drawer.classList.contains('active') && typeof drawer.renderContents === 'function') {
      drawer.renderContents(state);
      return;
    }

    // Drawer is closed (e.g. a gift sync on page load): swap the rendered
    // sections in silently. Dawn's renderContents would call open() and pop
    // the drawer at the visitor unasked.
    var parse = function (html, selector) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(selector);
    };
    var fresh = parse(state.sections['cart-drawer'] || '', '#CartDrawer');
    var current = document.getElementById('CartDrawer');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
      var overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
    }
    var bubbleHtml = state.sections['cart-icon-bubble'];
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      var freshBubble = parse(bubbleHtml, '.shopify-section');
      if (freshBubble) bubble.innerHTML = freshBubble.innerHTML;
    }
    if (typeof state.item_count === 'number') drawer.classList.toggle('is-empty', state.item_count === 0);
    else drawer.classList.remove('is-empty');
  }

  // Returns a promise resolving true when it changed the cart (a re-render
  // follows, so the caller should stop and let the observer run again).
  function syncGift(cart) {
    if (!giftActive() || giftState.syncing) return Promise.resolve(false);
    var line = giftLine(cart);
    var qualifies = qualifyingTotal(cart) >= cfg.gift.threshold;

    // The shopper removed the gift themselves while still qualifying —
    // respect that for the rest of the session instead of forcing it back.
    if (!line && giftState.hadGift && qualifies) {
      giftState.declined = true;
      rememberGift('sg-gift-declined', true);
      return Promise.resolve(false);
    }
    giftState.hadGift = !!line;

    if (qualifies && !line && (cart.item_count || 0) > 0) {
      giftState.syncing = true;
      return cartRequest('cart/add.js', {
        items: [{ id: Number(cfg.gift.variantId), quantity: 1, properties: { _sg_gift: '1' } }],
        sections: SECTIONS, sections_url: window.location.pathname
      }).then(function (state) {
        giftState.syncing = false;
        if (state.status) throw new Error(state.description || state.message);
        var added = (state.items && state.items[0]) || state;
        if (added && typeof added.final_line_price === 'number' && added.final_line_price > 0) {
          // The automatic discount is not making it free. Never charge for a
          // "gift": take it straight back out and disable the feature.
          giftState.broken = true;
          rememberGift('sg-gift-broken', true);
          return cartRequest('cart/change.js', { id: added.key, quantity: 0, sections: SECTIONS, sections_url: window.location.pathname })
            .then(function (s2) { rerender(s2); return true; });
        }
        giftState.hadGift = true;
        rerender(state);
        return true;
      }).catch(function () { giftState.syncing = false; return false; });
    }

    if (!qualifies && line) {
      giftState.syncing = true;
      giftState.hadGift = false;
      return cartRequest('cart/change.js', { id: line.key, quantity: 0, sections: SECTIONS, sections_url: window.location.pathname })
        .then(function (state) { giftState.syncing = false; rerender(state); return true; })
        .catch(function () { giftState.syncing = false; return false; });
    }
    return Promise.resolve(false);
  }

  function decorateGiftLine(drawer, cart) {
    var items = cart.items || [];
    for (var i = 0; i < items.length; i++) {
      var p = items[i].properties || {};
      var row = drawer.querySelector('#CartDrawer-Item-' + (i + 1));
      if (!row) continue;

      // Shopify allocates the Buy-X-Get-Y discount to the qualifying lines
      // too, at £0, and Dawn prints every allocation's title — so the jar
      // that merely counted towards the tier was labelled "Free Face Cream
      // at £100". Drop the zero-amount labels; only the real saving stays.
      var allocs = items[i].line_level_discount_allocations || [];
      var labels = row.querySelectorAll('.cart-item__details .discounts .discounts__discount');
      for (var a = allocs.length - 1; a >= 0; a--) {
        if (allocs[a].amount === 0 && labels[a]) labels[a].remove();
      }

      if (!p._sg_gift) continue;
      if (row.classList.contains('sg-cd-gift-line')) continue;
      row.classList.add('sg-cd-gift-line');
      var name = row.querySelector('.cart-item__name');
      if (name && !row.querySelector('.sg-cd-gift-tag')) {
        name.insertAdjacentHTML('afterend', '<span class="sg-cd-gift-tag">Free gift</span>');
      }
      var qty = row.querySelector('quantity-input');
      if (qty) qty.classList.add('sg-cd-gift-line__qty');
    }
  }

  /* ----------------------------------------------------- 3. spend bar */

  function buildBar(cart) {
    var ship = cfg.shipThreshold || 0;
    var gift = giftActive() ? cfg.gift : null;
    var giftT = gift ? gift.threshold : 0;
    if (!ship && !giftT) return null;

    var total = qualifyingTotal(cart);
    var max = Math.max(ship, giftT);
    var pct = Math.max(0, Math.min(100, (total / max) * 100));
    var shipMet = ship ? total >= ship : false;
    var giftMet = giftT ? total >= giftT : false;
    var allMet = (ship ? shipMet : true) && (giftT ? giftMet : true);

    var wrap = el('div', 'sg-cd-bar' + (allMet ? ' sg-cd-bar--done' : ''));
    wrap.setAttribute('data-sg-ship', '');

    var giftName = gift ? escapeHtml(gift.title) : '';
    var giftPrice = gift ? formatMoney(gift.price) : '';
    var text;
    if (ship && !shipMet) {
      text = 'Add <strong class="sg-cd-bar__amount">' + formatMoney(ship - total) + '</strong> more for free ' + escapeHtml(cfg.strings.deliveryName || 'delivery');
    } else if (giftT && !giftMet) {
      text = (ship ? '<strong>Free ' + escapeHtml(cfg.strings.deliveryName || 'delivery') + ' unlocked.</strong> ' : '') +
        'Add <strong class="sg-cd-bar__amount">' + formatMoney(giftT - total) + '</strong> more for a free ' + giftName +
        ' <span class="sg-cd-bar__was">(' + giftPrice + ')</span>';
    } else if (giftT && giftMet && giftLine(cart)) {
      text = '<strong>' + (ship ? 'Free ' + escapeHtml(cfg.strings.deliveryName || 'delivery') + ' and your free ' : 'Your free ') + giftName + ' are in.</strong>';
    } else if (ship && shipMet) {
      text = '<strong>Free ' + escapeHtml(cfg.strings.deliveryName || 'delivery') + ' unlocked.</strong>';
    } else {
      return null;
    }

    var markers = '';
    if (ship) markers += '<span class="sg-cd-bar__marker' + (shipMet ? ' is-met' : '') + '" style="left:' + ((ship / max) * 100) + '%"></span>';
    if (giftT) markers += '<span class="sg-cd-bar__marker' + (giftMet ? ' is-met' : '') + '" style="left:' + ((giftT / max) * 100) + '%"></span>';

    var legend = '<span class="sg-cd-bar__legend-zero">' + formatMoney(0, true) + '</span>';
    if (ship) legend += '<span class="sg-cd-bar__legend-ship' + (shipMet ? ' is-met' : '') + '">' + formatMoney(ship, true) + ' · ' + escapeHtml(cfg.strings.deliveryShort || 'Delivery') + '</span>';
    if (giftT) legend += '<span class="sg-cd-bar__legend-gift' + (giftMet ? ' is-met' : '') + '">' + formatMoney(giftT, true) + ' · Gift</span>';

    wrap.innerHTML =
      '<p class="sg-cd-bar__label" aria-live="polite">' + text + '</p>' +
      '<div class="sg-cd-bar__track"><span class="sg-cd-bar__fill" style="width:' + pct + '%"></span>' + markers + '</div>' +
      '<div class="sg-cd-bar__legend">' + legend + '</div>';
    return wrap;
  }

  /* ------------------------------------------------- 4. discount code */

  function appliedCodes(cart) {
    return (cart.discount_codes || []).filter(function (c) { return c.applicable; }).map(function (c) { return c.code; });
  }

  function buildDiscount(cart) {
    if (!cfg.show || !cfg.show.discount) return null;
    var wrap = el('div', 'sg-cd-discount');
    wrap.setAttribute('data-sg-discount', '');

    var chips = appliedCodes(cart).map(function (code) {
      return '<span class="sg-cd-discount__chip">' + escapeHtml(code) +
        '<button type="button" class="sg-cd-discount__remove" data-sg-discount-remove="' + escapeHtml(code) + '" aria-label="Remove discount code ' + escapeHtml(code) + '">×</button></span>';
    }).join('');

    wrap.innerHTML =
      '<form class="sg-cd-discount__form' + (discountState.error ? ' has-error' : '') + '" data-sg-discount-form novalidate>' +
        '<label class="visually-hidden" for="SgDiscountInput">Discount code</label>' +
        '<span class="sg-cd-discount__field">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12l-8 8-9-9V4h7l10 8z"></path><circle cx="7.5" cy="7.5" r="1.2"></circle></svg>' +
          '<input id="SgDiscountInput" class="sg-cd-discount__input" type="text" name="discount" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="Discount code" value="' + escapeHtml(discountState.pending) + '">' +
        '</span>' +
        '<button type="submit" class="sg-cd-discount__apply"><span data-sg-discount-label>Apply</span></button>' +
      '</form>' +
      (discountState.error ? '<p class="sg-cd-discount__error" role="alert">' + escapeHtml(discountState.error) + '</p>' : '') +
      (chips ? '<div class="sg-cd-discount__applied">' + chips + '</div>' : '');
    return wrap;
  }

  function applyDiscount(form) {
    var input = form.querySelector('input');
    var button = form.querySelector('button');
    var code = (input.value || '').trim();
    if (!code) { input.focus(); return; }

    var cart = lastCart || {};
    var codes = appliedCodes(cart);
    if (codes.indexOf(code.toUpperCase()) !== -1 || codes.indexOf(code) !== -1) {
      discountState.error = 'That code is already applied.';
      discountState.pending = '';
      refresh();
      return;
    }
    codes.push(code);

    button.disabled = true;
    button.classList.add('is-loading');
    form.querySelector('[data-sg-discount-label]').textContent = 'Applying…';

    cartRequest('cart/update.js', { discount: codes.join(','), sections: SECTIONS, sections_url: window.location.pathname })
      .then(function (state) {
        if (state.status) throw new Error(state.description || state.message);
        if (!state.discount_codes) {
          // API without discount support: the classic redirect still works.
          window.location.href = root + 'discount/' + encodeURIComponent(code) + '?redirect=' + encodeURIComponent(window.location.pathname);
          return;
        }
        var match = state.discount_codes.filter(function (c) { return c.code.toUpperCase() === code.toUpperCase(); })[0];
        if (match && match.applicable) {
          discountState.error = '';
          discountState.pending = '';
          rerender(state);
          return;
        }
        // Not applicable: drop it again so a dead code doesn't linger on the cart.
        discountState.error = "That code isn't valid for this order.";
        discountState.pending = code;
        var keep = appliedCodes(state);
        return cartRequest('cart/update.js', { discount: keep.join(','), sections: SECTIONS, sections_url: window.location.pathname })
          .then(function (s2) { rerender(s2); });
      })
      .catch(function () {
        discountState.error = "Couldn't apply that code right now.";
        discountState.pending = code;
        refresh();
      });
  }

  function removeDiscount(code) {
    var keep = appliedCodes(lastCart || {}).filter(function (c) { return c !== code; });
    discountState.error = '';
    cartRequest('cart/update.js', { discount: keep.join(','), sections: SECTIONS, sections_url: window.location.pathname })
      .then(function (state) { rerender(state); })
      .catch(function () { refresh(); });
  }

  /* ------------------------------------------- 5. delivery estimate */

  function buildDelivery() {
    if (!cfg.show || !cfg.show.delivery) return null;
    var wrap = el('div', 'sg-cd-delivery');
    wrap.setAttribute('data-sg-delivery', '');
    var icon = '<svg class="sg-cd-delivery__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z"></path><circle cx="7" cy="17.5" r="1.6"></circle><circle cx="17" cy="17.5" r="1.6"></circle></svg>';

    var estimated = (cfg.deliveryCountries || ['GB']).indexOf(cfg.country) !== -1;
    var info = estimated ? dispatchInfo() : null;

    if (!info) {
      wrap.innerHTML = icon + '<div class="sg-cd-delivery__body"><span class="sg-cd-delivery__lead">' + escapeHtml(cfg.strings.deliveryFallback || 'Delivery times are shown at checkout.') + '</span></div>';
      return wrap;
    }

    var lead = info.sameDay
      ? 'Order by <strong>' + cutoffLabel() + ' today</strong> and it arrives'
      : 'Dispatched <strong>' + info.dispatchLabel + '</strong>, arrives';
    wrap.innerHTML = icon +
      '<div class="sg-cd-delivery__body"><span class="sg-cd-delivery__lead">' + lead + '</span>' +
      '<span class="sg-cd-delivery__window">' + info.window + '</span></div>' +
      (cfg.strings.carrier ? '<span class="sg-cd-delivery__carrier">' + escapeHtml(cfg.strings.carrier) + '</span>' : '');
    return wrap;
  }

  /* -------------------------------------------------------------- upsell */

  function buildUpsell(cart) {
    if (!upsellConfig) return null;
    var template = document.getElementById('SgUpsellTemplate');
    if (!template || !('content' in template)) return null;

    var node = template.content.firstElementChild.cloneNode(true);
    var owned = {};
    (cart.items || []).forEach(function (item) { owned[String(item.product_id)] = true; });

    var max = upsellConfig.max || 2;
    var shown = 0;
    Array.prototype.slice.call(node.querySelectorAll('[data-sg-upsell-item]')).forEach(function (item) {
      if (owned[item.getAttribute('data-product-id')] || shown >= max) { item.remove(); return; }
      shown += 1;
    });
    if (shown === 0) return null;
    return node;
  }

  function addVariant(button, variantId) {
    var label = button.querySelector('[data-sg-upsell-label]');
    var original = label ? label.textContent : '';
    button.disabled = true;
    button.classList.add('is-loading');

    cartRequest('cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }], sections: SECTIONS, sections_url: window.location.pathname })
      .then(function (state) {
        if (state.status) throw new Error(state.description || state.message);
        if (label) label.textContent = upsellConfig.addedLabel || 'Added';
        rerender(state);
      })
      .catch(function () {
        button.disabled = false;
        button.classList.remove('is-loading');
        if (label) label.textContent = original;
      });
  }

  /* ------------------------------------------------------------- inject */

  function inject(cart) {
    lastCart = cart;
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var footer = drawer.querySelector('.drawer__footer');
    if (!footer) return;

    // Marks this render as done BEFORE any async work, so a gift sync that
    // triggers a re-render cannot race a second pass on the same footer.
    footer.setAttribute('data-sg-enhanced', '1');

    if (!cart.item_count) return; // Dawn's empty state — nothing to attach to.

    syncGift(cart).then(function (changed) {
      if (changed) return; // a fresh render is on its way; the observer re-runs inject
      paint(drawer, footer, cart);
    });
  }

  function paint(drawer, footer, cart) {
    ['[data-sg-countdown]', '[data-sg-trust]', '[data-sg-ship]', '[data-sg-upsell]', '[data-sg-discount]', '[data-sg-delivery]'].forEach(function (sel) {
      Array.prototype.forEach.call(drawer.querySelectorAll(sel), function (n) { n.remove(); });
    });

    // Header side.
    var items = drawer.querySelector('cart-drawer-items');
    if (items) {
      var head = el('div', 'sg-cd-head');
      head.setAttribute('data-sg-head', '');
      var cd = buildCountdown();
      var tr = buildTrust();
      if (cd) head.appendChild(cd);
      if (tr) head.appendChild(tr);
      var oldHead = drawer.querySelector('[data-sg-head]');
      if (oldHead) oldHead.remove();
      if (head.childNodes.length) items.parentNode.insertBefore(head, items);
    }

    decorateGiftLine(drawer, cart);

    // Footer side, in reading order.
    var blocks = [buildBar(cart), buildUpsell(cart), buildDiscount(cart), buildDelivery()].filter(Boolean);
    var anchor = footer.firstChild;
    blocks.forEach(function (b) { footer.insertBefore(b, anchor); });

    if (cfg.show && cfg.show.countdown) startClock();

    // After a rejected code the field is rebuilt; hand focus back so the
    // shopper can correct it without hunting for the input again.
    if (discountState.error && drawer.classList.contains('active')) {
      var input = footer.querySelector('#SgDiscountInput');
      if (input) { try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); } }
    }
  }

  function refresh() {
    busy = true;
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(inject)
      .catch(function () { /* cart unreachable — leave the stock drawer alone */ })
      .then(function () { busy = false; });
  }

  function start() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    refresh();

    drawer.addEventListener('click', function (event) {
      var up = event.target.closest('[data-sg-upsell-add]');
      if (up) {
        var item = up.closest('[data-sg-upsell-item]');
        if (!item) return;
        event.preventDefault();
        addVariant(up, item.getAttribute('data-variant-id'));
        return;
      }
      var rm = event.target.closest('[data-sg-discount-remove]');
      if (rm) {
        event.preventDefault();
        removeDiscount(rm.getAttribute('data-sg-discount-remove'));
      }
    });

    drawer.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-sg-discount-form]');
      if (!form) return;
      event.preventDefault();
      event.stopPropagation();
      applyDiscount(form);
    });

    drawer.addEventListener('input', function (event) {
      if (event.target && event.target.id === 'SgDiscountInput') {
        discountState.pending = event.target.value;
        var form = event.target.closest('[data-sg-discount-form]');
        if (form) form.classList.remove('has-error');
      }
    });

    if (!('MutationObserver' in window)) return;
    new MutationObserver(function () {
      if (busy) return;
      var footer = drawer.querySelector('.drawer__footer');
      if (!footer || footer.hasAttribute('data-sg-enhanced')) return;
      refresh();
    }).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
